import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MessageCircleOff, Timer, Users, Lock, Save } from "lucide-react";

/**
 * ChannelChatSettings — streamer dashboard panel to tune chat behavior.
 * All settings are server-enforced on chat WS.
 */
export default function ChannelChatSettings({ ownerUsername }) {
  const [settings, setSettings] = useState(null);
  const [slow, setSlow] = useState(0);
  const [chatOff, setChatOff] = useState(false);
  const [mode, setMode] = useState("public");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!ownerUsername) return;
    api.get(`/channels/${ownerUsername}/chat-settings`).then(r => {
      setSettings(r.data);
      setSlow(r.data.slow_mode_sec || 0);
      setChatOff(!!r.data.chat_off);
      setMode(r.data.mode || "public");
    }).catch(() => {});
  }, [ownerUsername]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/channels/${ownerUsername}/chat-settings`, {
        slow_mode_sec: Math.max(0, Math.min(600, Number(slow) || 0)),
        chat_off: chatOff,
        mode,
      });
      setSettings(r.data);
      toast.success("Chat settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!settings) return null;

  return (
    <div className="sc-card p-5" data-testid="channel-chat-settings">
      <div className="sc-label mb-1">Chat settings</div>
      <h3 className="sc-heading text-xl font-bold mb-1">Chat moderation</h3>
      <p className="text-xs text-zinc-500 mb-5 sc-mono">Real-time enforcement on incoming chat messages. Owner &amp; admins bypass all rules.</p>

      {/* Chat off */}
      <label className="flex items-center gap-3 p-3 rounded-sm border border-white/10 hover:border-white/20 transition cursor-pointer mb-2">
        <input
          data-testid="chat-setting-off"
          type="checkbox"
          checked={chatOff}
          onChange={e => setChatOff(e.target.checked)}
          className="w-4 h-4 accent-[color:var(--sc-pink)]"
        />
        <MessageCircleOff className="w-4 h-4 text-[color:var(--sc-pink)]" />
        <div className="flex-1">
          <div className="text-sm font-bold">Turn chat OFF</div>
          <div className="text-xs text-zinc-500 sc-mono">Nobody but you and admins can send messages.</div>
        </div>
      </label>

      {/* Slow mode */}
      <div className="p-3 rounded-sm border border-white/10 mb-2">
        <div className="flex items-center gap-3">
          <Timer className="w-4 h-4 text-[color:var(--sc-volt)]" />
          <div className="flex-1">
            <div className="text-sm font-bold">Slow mode</div>
            <div className="text-xs text-zinc-500 sc-mono">Seconds between messages per user. 0 = off.</div>
          </div>
          <input
            data-testid="chat-setting-slow"
            type="number"
            min={0}
            max={600}
            value={slow}
            onChange={e => setSlow(e.target.value)}
            className="sc-input w-24 text-right"
          />
          <span className="text-xs text-zinc-500 sc-mono">sec</span>
        </div>
      </div>

      {/* Mode */}
      <div className="p-3 rounded-sm border border-white/10">
        <div className="flex items-center gap-3 mb-2">
          <Lock className="w-4 h-4 text-[color:var(--sc-cyan)]" />
          <div className="flex-1">
            <div className="text-sm font-bold">Who can chat</div>
            <div className="text-xs text-zinc-500 sc-mono">Gate incoming chat by relationship.</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { id: "public",             icon: Users,        label: "Everyone",    color: "#CCFF00" },
            { id: "followers_only",     icon: Users,        label: "Followers",   color: "#00E5FF" },
            { id: "subscribers_only",   icon: Users,        label: "Subs only",   color: "#FF1053" },
          ].map(opt => (
            <button
              key={opt.id}
              type="button"
              data-testid={`chat-setting-mode-${opt.id}`}
              onClick={() => setMode(opt.id)}
              className="p-3 rounded-sm border transition text-center"
              style={{
                borderColor: mode === opt.id ? opt.color : "rgba(255,255,255,0.10)",
                background: mode === opt.id ? `${opt.color}12` : "transparent",
              }}
            >
              <opt.icon className="w-4 h-4 mx-auto mb-1" style={{ color: opt.color }} />
              <div className="text-[11px] sc-mono uppercase tracking-widest">{opt.label}</div>
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={save}
        disabled={busy}
        data-testid="chat-settings-save"
        className="sc-btn text-sm mt-4 inline-flex items-center gap-2"
      >
        <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save chat settings"}
      </button>
    </div>
  );
}
